import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { vitalityService } from '../services/vitalityService';
import {
  loadCache,
  saveCache,
  clearCache,
  subscribeToInvalidations
} from '../services/vitalityCache';
import {
  VitalityGroup,
  VitalityProgressEvent,
  VitalityRecommendations
} from '../types/vitality';

export interface UseVitalityRecommendationsState {
  data: VitalityRecommendations | null;
  /** Groups received progressively during streaming (used while loading). */
  partialGroups: VitalityGroup[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  /** Latest streaming progress event (stage + label). */
  progress: VitalityProgressEvent | null;
  /** True when cached data is shown but signature mismatch indicates a refresh
   *  is queued/in-flight. */
  stale: boolean;
  /** True when cache hit was used to render initial data. */
  fromCache: boolean;
  refresh: () => Promise<void>;
}

const RESERVED_PARTIAL_DURING_STREAM = true;

/**
 * Loads vitality recommendations with localStorage caching:
 * - Reads cache → renders immediately if present.
 * - Asks backend for current signature → if hash matches, done.
 * - Otherwise streams a fresh recommendation set (progressive UI).
 * - Listens for invalidation events (facts/sessions/favorites changes).
 */
export function useVitalityRecommendations(language?: 'en' | 'nl'): UseVitalityRecommendationsState {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [data, setData] = useState<VitalityRecommendations | null>(null);
  const [partialGroups, setPartialGroups] = useState<VitalityGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<VitalityProgressEvent | null>(null);
  const [stale, setStale] = useState(false);
  const [fromCache, setFromCache] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const stream = useCallback(
    async (mode: 'initial' | 'refresh') => {
      if (!userId) return;
      // Cancel any prior in-flight stream
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);
      setError(null);
      setPartialGroups([]);
      setProgress(null);

      try {
        const result = await vitalityService.streamRecommendations(
          {
            onProgress: ev => setProgress(ev),
            onPartial: ev => {
              if (!RESERVED_PARTIAL_DURING_STREAM) return;
              setPartialGroups(prev => {
                const idx = prev.findIndex(g => g.pillar === ev.group.pillar);
                if (idx >= 0) {
                  const copy = prev.slice();
                  copy[idx] = ev.group;
                  return copy;
                }
                return [...prev, ev.group];
              });
            }
          },
          ctrl.signal,
          language
        );
        if (ctrl.signal.aborted) return;
        setData(result);
        setStale(false);
        setFromCache(false);
        setPartialGroups([]);
        saveCache(userId, result);
      } catch (err: any) {
        if (ctrl.signal.aborted) return;
        const message = err?.message || 'Failed to load recommendations';
        setError(message);
      } finally {
        if (mode === 'initial') setLoading(false);
        else setRefreshing(false);
      }
    },
    [userId, language]
  );

  const refresh = useCallback(async () => {
    if (!userId) return;
    clearCache(userId);
    await stream('refresh');
  }, [userId, stream]);

  // Initial load: try cache, then signature check.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const cached = loadCache(userId);
    if (cached) {
      setData(cached.payload);
      setFromCache(true);
    }

    (async () => {
      try {
        const sig = await vitalityService.getSignature(language);
        if (cancelled) return;
        if (cached && cached.signalsHash === sig.signalsHash) {
          // Cache is current; nothing to do
          setStale(false);
          return;
        }
        // Either no cache or signature changed — refresh in background.
        if (cached) setStale(true);
        await stream(cached ? 'refresh' : 'initial');
      } catch (err: any) {
        if (cancelled) return;
        // If signature endpoint fails and we have cache, keep showing cache.
        if (!cached) {
          setError(err?.message || 'Failed to load recommendations');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, language]);

  // Subscribe to invalidation triggers (facts/sessions/favorites mutations).
  useEffect(() => {
    if (!userId) return;
    const unsub = subscribeToInvalidations(userId, () => {
      setStale(true);
      // Eagerly refresh in background
      stream('refresh').catch(() => {
        /* swallow */
      });
    });
    return unsub;
  }, [userId, stream]);

  // Cancel stream on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return {
    data,
    partialGroups,
    loading,
    refreshing,
    error,
    progress,
    stale,
    fromCache,
    refresh
  };
}
